/* ═══════════════════════════════════════════════════════════════════════
 * facemeet-ui.js — Phase 2 of the redesign
 *
 *   Provides the theme manager + all UI primitives the redesigned
 *   screens compose from. Consumes only Phase 1 tokens.
 *
 *   USAGE:
 *     // Once, anywhere (App.js is fine):
 *     import { ToastViewport } from "./styles/facemeet-ui";
 *     <ToastViewport />          // mount once, anywhere
 *
 *     // Then anywhere in your tree:
 *     import {
 *       Button, Input, Modal, Spinner,
 *       ThemeToggle, useTheme, toast,
 *     } from "./styles/facemeet-ui";
 *
 *     toast.success("Meeting created");
 *     <Button variant="primary" loading={isCreating}>Start Meeting</Button>
 *
 *   STYLE INJECTION: the CSS for these primitives is injected once into
 *   <head> on first import. Idempotent — safe to import in any number
 *   of files. SSR-safe (guarded by typeof document).
 *
 *   No external deps. No Framer Motion required (we lean on CSS
 *   transitions for now). If you later add Framer, it slots in cleanly.
 * ═══════════════════════════════════════════════════════════════════ */

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

/* ═══════════════════════════════════════════════════════════════════════
   STYLES — injected once, scoped via .fm- prefix to avoid collisions.
   Every value here is a token from facemeet-theme.css (Phase 1).
═══════════════════════════════════════════════════════════════════════ */
const STYLES = `
/* ── Spinner ─────────────────────────────────────────────────────────── */
.fm-spinner {
  display: inline-block;
  border-radius: 50%;
  border: 2px solid var(--border-default);
  border-top-color: var(--accent);
  animation: fm-spin 0.7s linear infinite;
  vertical-align: middle;
}
.fm-spinner--xs { width: 12px; height: 12px; border-width: 1.5px; }
.fm-spinner--sm { width: 16px; height: 16px; }
.fm-spinner--md { width: 22px; height: 22px; }
.fm-spinner--lg { width: 36px; height: 36px; border-width: 3px; }

/* ── Theme Toggle ────────────────────────────────────────────────────── */
.fm-theme-toggle {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: var(--radius-md);
  background: var(--bg-surface-2);
  border: 1px solid var(--border-default);
  color: var(--text-muted);
  cursor: pointer;
  overflow: hidden;
  transition:
    background-color var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out),
    color var(--duration-fast) var(--ease-out),
    transform var(--duration-fast) var(--ease-out);
}
.fm-theme-toggle:hover {
  background: var(--bg-surface-3);
  border-color: var(--border-strong);
  color: var(--text-strong);
  transform: translateY(-1px);
}
.fm-theme-toggle:active { transform: translateY(0); }

.fm-theme-toggle__icon {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  transition:
    opacity var(--duration-base) var(--ease-out),
    transform var(--duration-base) var(--ease-out);
}
.fm-theme-toggle__icon[data-state="hidden"] {
  opacity: 0;
  transform: rotate(-45deg) scale(0.6);
  pointer-events: none;
}
.fm-theme-toggle__icon[data-state="shown"] {
  opacity: 1;
  transform: rotate(0) scale(1);
}

/* ── Button ─────────────────────────────────────────────────────────── */
.fm-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  font-family: var(--font-sans);
  font-weight: var(--weight-semibold);
  letter-spacing: -0.005em;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
  position: relative;
  isolation: isolate;
  transition:
    background-color var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out),
    color var(--duration-fast) var(--ease-out),
    box-shadow var(--duration-fast) var(--ease-out),
    transform var(--duration-fast) var(--ease-out);
}
.fm-btn:not(:disabled):hover { transform: translateY(-1px); }
.fm-btn:not(:disabled):active { transform: translateY(0); transition-duration: var(--duration-instant); }
.fm-btn:disabled,
.fm-btn[aria-disabled="true"] { opacity: 0.5; cursor: not-allowed; transform: none !important; }

/* Sizes */
.fm-btn--sm { height: 32px; padding: 0 var(--space-3); font-size: var(--text-sm); }
.fm-btn--md { height: 40px; padding: 0 var(--space-4); font-size: var(--text-base); }
.fm-btn--lg { height: 48px; padding: 0 var(--space-5); font-size: var(--text-md); border-radius: var(--radius-lg); }

/* Variants */
.fm-btn--primary {
  background: var(--accent);
  color: var(--text-on-accent);
  box-shadow: var(--shadow-sm);
}
.fm-btn--primary:not(:disabled):hover { background: var(--accent-hover); box-shadow: var(--shadow-glow-accent); }
.fm-btn--primary:not(:disabled):active { background: var(--accent-active); }

.fm-btn--secondary {
  background: var(--bg-surface);
  border-color: var(--border-strong);
  color: var(--text-strong);
  box-shadow: var(--shadow-xs);
}
.fm-btn--secondary:not(:disabled):hover {
  background: var(--bg-surface-2);
  border-color: var(--border-strong);
  box-shadow: var(--shadow-sm);
}

.fm-btn--ghost {
  background: transparent;
  color: var(--text-default);
}
.fm-btn--ghost:not(:disabled):hover {
  background: var(--bg-surface-2);
  color: var(--text-strong);
}

.fm-btn--danger {
  background: var(--danger);
  color: white;
}
.fm-btn--danger:not(:disabled):hover { background: var(--danger-600); }

.fm-btn--icon {
  width: 40px;
  padding: 0;
  background: var(--bg-surface-2);
  border-color: var(--border-default);
  color: var(--text-muted);
}
.fm-btn--icon.fm-btn--sm { width: 32px; }
.fm-btn--icon.fm-btn--lg { width: 48px; }
.fm-btn--icon:not(:disabled):hover {
  background: var(--bg-surface-3);
  color: var(--text-strong);
  border-color: var(--border-strong);
}

.fm-btn--full { width: 100%; }

.fm-btn__loader-wrap {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}

/* ── Input ──────────────────────────────────────────────────────────── */
.fm-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.fm-field__label-row {
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  color: var(--text-muted);
}

.fm-field__label { color: var(--text-default); }
.fm-field__req { color: var(--danger); margin-left: 2px; }
.fm-field__optional { font-weight: var(--weight-regular); color: var(--text-faint); }

.fm-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--bg-surface-2);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  transition:
    background-color var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out),
    box-shadow var(--duration-fast) var(--ease-out);
}
.fm-input-wrap:hover:not(.fm-input-wrap--disabled) { border-color: var(--border-strong); }
.fm-input-wrap:focus-within {
  background: var(--bg-surface);
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-soft);
}
.fm-input-wrap--invalid { border-color: var(--danger); }
.fm-input-wrap--invalid:focus-within {
  border-color: var(--danger);
  box-shadow: 0 0 0 3px var(--danger-soft);
}
.fm-input-wrap--disabled { opacity: 0.6; pointer-events: none; }

.fm-input {
  flex: 1;
  min-width: 0;
  height: 42px;
  padding: 0 var(--space-3);
  background: transparent;
  border: none;
  outline: none;
  color: var(--text-strong);
  font-size: var(--text-base);
  font-family: inherit;
  -webkit-appearance: none;
  appearance: none;
}
.fm-input::placeholder { color: var(--text-faint); }

/* On mobile, prevent iOS auto-zoom by ensuring font-size >= 16px */
@media (max-width: 767px) {
  .fm-input { font-size: 16px; }
}

.fm-input-wrap__slot {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--space-2) 0 var(--space-3);
  color: var(--text-faint);
  flex-shrink: 0;
}
.fm-input-wrap__slot--trailing { padding: 0 var(--space-3) 0 var(--space-2); }
.fm-input-wrap__slot--button {
  padding: 0;
  margin-right: var(--space-1);
}
.fm-input-wrap__slot button {
  width: 32px; height: 32px;
  border-radius: var(--radius-sm);
  color: var(--text-faint);
  transition: background-color var(--duration-fast) var(--ease-out), color var(--duration-fast) var(--ease-out);
}
.fm-input-wrap__slot button:hover { background: var(--bg-surface-3); color: var(--text-strong); }

.fm-input-wrap--with-leading .fm-input { padding-left: 0; }
.fm-input-wrap--with-trailing .fm-input { padding-right: 0; }

.fm-field__hint {
  font-size: var(--text-xs);
  color: var(--text-muted);
  margin-top: 2px;
  line-height: var(--leading-snug);
}
.fm-field__error {
  font-size: var(--text-xs);
  color: var(--danger);
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

/* Textarea variant */
.fm-input--textarea {
  min-height: 96px;
  max-height: 240px;
  padding: var(--space-3);
  resize: vertical;
  line-height: var(--leading-normal);
}

/* ── Modal ──────────────────────────────────────────────────────────── */
.fm-modal-overlay {
  position: fixed;
  inset: 0;
  background: var(--bg-overlay);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: var(--z-modal);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4);
  animation: fm-fade-in var(--duration-base) var(--ease-out);
}

.fm-modal {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  background: var(--bg-surface);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
  max-height: calc(100dvh - var(--space-8));
  overflow: hidden;
  animation: fm-scale-in var(--duration-slow) var(--ease-spring);
}
.fm-modal--sm { max-width: 380px; }
.fm-modal--md { max-width: 520px; }
.fm-modal--lg { max-width: 720px; }
.fm-modal--xl { max-width: 920px; }

.fm-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-5) var(--space-6) var(--space-3);
  flex-shrink: 0;
}
.fm-modal__title {
  font-size: var(--text-lg);
  font-weight: var(--weight-bold);
  color: var(--text-strong);
  letter-spacing: var(--tracking-tight);
}
.fm-modal__close {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-sm);
  color: var(--text-muted);
  transition: background-color var(--duration-fast) var(--ease-out), color var(--duration-fast) var(--ease-out);
  flex-shrink: 0;
}
.fm-modal__close:hover { background: var(--bg-surface-2); color: var(--text-strong); }

.fm-modal__body {
  padding: 0 var(--space-6) var(--space-5);
  overflow-y: auto;
  color: var(--text-default);
  line-height: var(--leading-normal);
}
.fm-modal__body--no-pad { padding: 0; }

.fm-modal__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-2);
  padding: var(--space-4) var(--space-6);
  background: var(--bg-surface-2);
  border-top: 1px solid var(--border-default);
  flex-shrink: 0;
  flex-wrap: wrap;
}

@media (max-width: 480px) {
  .fm-modal { border-radius: var(--radius-lg); }
  .fm-modal__header { padding: var(--space-4) var(--space-4) var(--space-2); }
  .fm-modal__body { padding: 0 var(--space-4) var(--space-4); }
  .fm-modal__footer { padding: var(--space-3) var(--space-4); }
}

/* Bottom sheet variant on mobile — opt in via mobileSheet prop */
@media (max-width: 560px) {
  .fm-modal-overlay--sheet { align-items: flex-end; padding: 0; }
  .fm-modal--sheet {
    max-width: 100%;
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    animation: fm-slide-up var(--duration-slow) var(--ease-out);
    max-height: 90dvh;
    padding-bottom: env(safe-area-inset-bottom, 0);
  }
}

/* ── Toast ──────────────────────────────────────────────────────────── */
.fm-toast-viewport {
  position: fixed;
  top: var(--space-4);
  left: 50%;
  transform: translateX(-50%);
  z-index: var(--z-toast);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  pointer-events: none;
  width: max-content;
  max-width: calc(100vw - var(--space-6));
}
@media (max-width: 560px) {
  .fm-toast-viewport { top: max(var(--space-3), env(safe-area-inset-top, 0)); }
}

.fm-toast {
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  min-width: 280px;
  max-width: min(420px, calc(100vw - var(--space-6)));
  padding: var(--space-3) var(--space-4);
  background: var(--bg-surface);
  border: 1px solid var(--border-default);
  border-left: 3px solid var(--accent);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  color: var(--text-strong);
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  animation: fm-slide-down var(--duration-base) var(--ease-out);
}
.fm-toast--success { border-left-color: var(--success); }
.fm-toast--warning { border-left-color: var(--warning); }
.fm-toast--danger  { border-left-color: var(--danger); }
.fm-toast--info    { border-left-color: var(--info); }

.fm-toast__icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}
.fm-toast--success .fm-toast__icon { color: var(--success); }
.fm-toast--warning .fm-toast__icon { color: var(--warning); }
.fm-toast--danger .fm-toast__icon  { color: var(--danger); }
.fm-toast--info .fm-toast__icon    { color: var(--info); }

.fm-toast__body { flex: 1; min-width: 0; }
.fm-toast__title { font-weight: var(--weight-semibold); margin-bottom: 1px; }
.fm-toast__message { color: var(--text-default); word-break: break-word; }

.fm-toast__close {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: var(--radius-xs);
  color: var(--text-faint);
  margin-top: -2px;
}
.fm-toast__close:hover { background: var(--bg-surface-2); color: var(--text-strong); }

.fm-toast--exiting { animation: fm-fade-out var(--duration-fast) var(--ease-in) forwards; }
`;

/* ── Inject styles once ──────────────────────────────────────────────── */
let _stylesInjected = false;
const injectStyles = () => {
  if (_stylesInjected || typeof document === "undefined") return;
  if (document.getElementById("fm-ui-styles")) { _stylesInjected = true; return; }
  const tag = document.createElement("style");
  tag.id = "fm-ui-styles";
  tag.textContent = STYLES;
  document.head.appendChild(tag);
  _stylesInjected = true;
};
if (typeof document !== "undefined") injectStyles();

/* ═══════════════════════════════════════════════════════════════════════
   UTILITIES
═══════════════════════════════════════════════════════════════════════ */
/** Merge classNames safely, skipping falsy values. */
export const cn = (...args) => args.filter(Boolean).join(" ");

/* ═══════════════════════════════════════════════════════════════════════
   ICONS — minimal, accessible, currentColor-driven
═══════════════════════════════════════════════════════════════════════ */
const Svg = ({ children, size = 18, ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...rest}
  >
    {children}
  </svg>
);

export const SunIcon = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </Svg>
);

export const MoonIcon = (p) => (
  <Svg {...p}>
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </Svg>
);

export const SystemIcon = (p) => (
  <Svg {...p}>
    <rect x="3" y="4" width="18" height="13" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </Svg>
);

export const XIcon = (p) => (
  <Svg {...p}>
    <path d="M18 6L6 18M6 6l12 12" />
  </Svg>
);

export const CheckIcon = (p) => (
  <Svg {...p}>
    <path d="M20 6L9 17l-5-5" />
  </Svg>
);

export const AlertIcon = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v4M12 16h.01" />
  </Svg>
);

export const InfoIcon = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4M12 8h.01" />
  </Svg>
);

/* ═══════════════════════════════════════════════════════════════════════
   THEME SYSTEM
   
   The applied theme is written to <html data-theme="…"> so the Phase 1
   CSS variables flip everywhere. `system` clears the attribute so the
   prefers-color-scheme media query in the foundation takes over.
═══════════════════════════════════════════════════════════════════════ */
const THEME_KEY = "fm_theme";          // user choice: 'light' | 'dark' | 'system'
const VALID_THEMES = new Set(["light", "dark", "system"]);

/** Compute what theme is actually rendered, given user choice. */
const resolveTheme = (choice) => {
  if (choice === "light" || choice === "dark") return choice;
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

/** Apply the theme to <html>. `system` removes the attribute. */
const applyTheme = (choice) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (choice === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", choice);
};

/* Pub/sub so multiple components stay in sync without a Provider. */
const themeListeners = new Set();
const readStoredTheme = () => {
  if (typeof localStorage === "undefined") return "system";
  const v = localStorage.getItem(THEME_KEY);
  return VALID_THEMES.has(v) ? v : "system";
};

/* Boot: apply persisted choice immediately so there's no flash. This
   runs at module load — before React mounts — which prevents the dreaded
   "theme flicker" on first paint. */
if (typeof document !== "undefined") {
  applyTheme(readStoredTheme());
}

/* Watch OS preference changes — only matters when user is on 'system'. */
if (typeof window !== "undefined" && window.matchMedia) {
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  const handler = () => {
    if (readStoredTheme() === "system") {
      themeListeners.forEach((l) => l());
    }
  };
  // Safari < 14 uses addListener; modern uses addEventListener
  if (mq.addEventListener) mq.addEventListener("change", handler);
  else if (mq.addListener) mq.addListener(handler);
}

/* Cross-tab sync — log out / change theme in tab A reflects in tab B. */
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === THEME_KEY) {
      applyTheme(readStoredTheme());
      themeListeners.forEach((l) => l());
    }
  });
}

/**
 * useTheme — read + update the theme.
 *
 * Returns:
 *   theme         : user's choice — 'light' | 'dark' | 'system'
 *   resolvedTheme : what's actually rendered — 'light' | 'dark'
 *   setTheme(next): persist + apply a new choice
 *   toggleTheme() : convenience — cycles light → dark → light (skipping system)
 */
export function useTheme() {
  const [theme, setThemeState] = useState(() => readStoredTheme());
  const [resolved, setResolved] = useState(() => resolveTheme(theme));

  useEffect(() => {
    const sync = () => {
      const t = readStoredTheme();
      setThemeState(t);
      setResolved(resolveTheme(t));
    };
    themeListeners.add(sync);
    return () => themeListeners.delete(sync);
  }, []);

  const setTheme = useCallback((next) => {
    if (!VALID_THEMES.has(next)) return;
    try { localStorage.setItem(THEME_KEY, next); } catch (_) {}
    applyTheme(next);
    setThemeState(next);
    setResolved(resolveTheme(next));
    themeListeners.forEach((l) => l());
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(resolved === "dark" ? "light" : "dark");
  }, [resolved, setTheme]);

  return { theme, resolvedTheme: resolved, setTheme, toggleTheme };
}

/**
 * ThemeToggle — a single icon button that crossfades between sun/moon
 * based on the resolved theme. Click cycles light ↔ dark.
 *
 * To expose 'system' too, add a long-press handler or a dropdown — the
 * useTheme().setTheme('system') call is all you need.
 */
export function ThemeToggle({ className, size = "md", title }) {
  const { resolvedTheme, toggleTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  return (
    <button
      type="button"
      className={cn("fm-theme-toggle", className)}
      onClick={toggleTheme}
      title={title || (isDark ? "Switch to light mode" : "Switch to dark mode")}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
    >
      <span className="fm-theme-toggle__icon" data-state={isDark ? "shown" : "hidden"}>
        <MoonIcon size={18} />
      </span>
      <span className="fm-theme-toggle__icon" data-state={isDark ? "hidden" : "shown"}>
        <SunIcon size={18} />
      </span>
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   SPINNER — sizes: xs|sm|md|lg
═══════════════════════════════════════════════════════════════════════ */
export function Spinner({ size = "sm", className, ...rest }) {
  return (
    <span
      className={cn("fm-spinner", `fm-spinner--${size}`, className)}
      role="status"
      aria-label="Loading"
      {...rest}
    />
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   BUTTON
   
   Props:
     variant      : 'primary' | 'secondary' | 'ghost' | 'danger'  (default primary)
     size         : 'sm' | 'md' | 'lg'                            (default md)
     iconOnly     : boolean — square button, no label
     fullWidth    : boolean
     loading      : boolean — replaces leadingIcon with spinner, disables click
     leadingIcon  : ReactNode
     trailingIcon : ReactNode
     ...all native button props (onClick, type, etc.)
═══════════════════════════════════════════════════════════════════════ */
export const Button = forwardRef(function Button(
  {
    variant = "primary",
    size = "md",
    iconOnly = false,
    fullWidth = false,
    loading = false,
    leadingIcon,
    trailingIcon,
    disabled,
    className,
    children,
    type = "button",
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        "fm-btn",
        `fm-btn--${variant}`,
        `fm-btn--${size}`,
        iconOnly && "fm-btn--icon",
        fullWidth && "fm-btn--full",
        className,
      )}
      {...rest}
    >
      <span className="fm-btn__loader-wrap">
        {loading ? <Spinner size={size === "sm" ? "xs" : "sm"} /> : leadingIcon}
        {!iconOnly && children}
        {!loading && trailingIcon}
      </span>
    </button>
  );
});

/* ═══════════════════════════════════════════════════════════════════════
   INPUT
   
   Props:
     label, hint, error, required, optional, id
     leadingIcon, trailingIcon, trailingButton (with onClick)
     multiline    : boolean — renders <textarea>
     ...all native input props
═══════════════════════════════════════════════════════════════════════ */
export const Input = forwardRef(function Input(
  {
    label,
    hint,
    error,
    required = false,
    optional = false,
    id,
    leadingIcon,
    trailingIcon,
    trailingButton,        // { icon, onClick, ariaLabel }
    multiline = false,
    rows = 3,
    disabled,
    className,
    ...rest
  },
  ref,
) {
  const generatedId = useId();
  const inputId = id || generatedId;
  const errorId = error ? `${inputId}-error` : undefined;
  const hintId = hint && !error ? `${inputId}-hint` : undefined;
  const describedBy = errorId || hintId;

  const Tag = multiline ? "textarea" : "input";

  return (
    <div className={cn("fm-field", className)}>
      {label && (
        <div className="fm-field__label-row">
          <label htmlFor={inputId} className="fm-field__label">
            {label}
            {required && <span className="fm-field__req" aria-hidden="true">*</span>}
          </label>
          {optional && <span className="fm-field__optional">optional</span>}
        </div>
      )}

      <div
        className={cn(
          "fm-input-wrap",
          leadingIcon && "fm-input-wrap--with-leading",
          (trailingIcon || trailingButton) && "fm-input-wrap--with-trailing",
          error && "fm-input-wrap--invalid",
          disabled && "fm-input-wrap--disabled",
        )}
      >
        {leadingIcon && (
          <span className="fm-input-wrap__slot" aria-hidden="true">{leadingIcon}</span>
        )}

        <Tag
          ref={ref}
          id={inputId}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={describedBy}
          aria-required={required || undefined}
          disabled={disabled}
          rows={multiline ? rows : undefined}
          className={cn("fm-input", multiline && "fm-input--textarea")}
          {...rest}
        />

        {trailingIcon && !trailingButton && (
          <span className="fm-input-wrap__slot fm-input-wrap__slot--trailing" aria-hidden="true">
            {trailingIcon}
          </span>
        )}

        {trailingButton && (
          <span className="fm-input-wrap__slot fm-input-wrap__slot--button">
            <button
              type="button"
              onClick={trailingButton.onClick}
              aria-label={trailingButton.ariaLabel || "Toggle"}
            >
              {trailingButton.icon}
            </button>
          </span>
        )}
      </div>

      {error ? (
        <span id={errorId} className="fm-field__error" role="alert">
          <AlertIcon size={12} /> {error}
        </span>
      ) : hint ? (
        <span id={hintId} className="fm-field__hint">{hint}</span>
      ) : null}
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════════════════
   MODAL
   
   Props:
     open                  : boolean
     onClose               : fn
     title                 : string
     size                  : 'sm' | 'md' | 'lg' | 'xl'   (default 'md')
     footer                : ReactNode (rendered in dimmed footer band)
     children              : modal body
     closeOnOverlayClick   : boolean (default true)
     closeOnEsc            : boolean (default true)
     mobileSheet           : boolean — slides up from bottom on phones
     hideHeader            : boolean — skip title bar entirely (custom layouts)
     bodyClassName         : extra class on .fm-modal__body (e.g., 'fm-modal__body--no-pad')
     initialFocusRef       : ref to focus on open (defaults to first focusable)
═══════════════════════════════════════════════════════════════════════ */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export function Modal({
  open,
  onClose,
  title,
  size = "md",
  footer,
  children,
  closeOnOverlayClick = true,
  closeOnEsc = true,
  mobileSheet = false,
  hideHeader = false,
  bodyClassName,
  initialFocusRef,
  className,
}) {
  const modalRef = useRef(null);
  const previouslyFocused = useRef(null);

  /* Scroll lock + remember focus */
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
      // Return focus to the element that opened the modal
      if (previouslyFocused.current && previouslyFocused.current.focus) {
        try { previouslyFocused.current.focus(); } catch (_) {}
      }
    };
  }, [open]);

  /* Initial focus + focus trap */
  useLayoutEffect(() => {
    if (!open || !modalRef.current) return;
    const root = modalRef.current;
    const target = initialFocusRef?.current
      || root.querySelector(FOCUSABLE_SELECTOR)
      || root;
    if (target?.focus) {
      try { target.focus({ preventScroll: true }); } catch (_) {}
    }
  }, [open, initialFocusRef]);

  /* Keyboard: Escape + Tab trap */
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && closeOnEsc) {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key === "Tab" && modalRef.current) {
        const focusables = Array.from(
          modalRef.current.querySelectorAll(FOCUSABLE_SELECTOR),
        ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);
        if (focusables.length === 0) { e.preventDefault(); return; }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, closeOnEsc, onClose]);

  if (!open || typeof document === "undefined") return null;

  const titleId = title ? "fm-modal-title" : undefined;

  return createPortal(
    <div
      className={cn("fm-modal-overlay", mobileSheet && "fm-modal-overlay--sheet")}
      onMouseDown={(e) => {
        // mousedown (not click) so a drag-select that ends outside doesn't close
        if (closeOnOverlayClick && e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          "fm-modal",
          `fm-modal--${size}`,
          mobileSheet && "fm-modal--sheet",
          className,
        )}
      >
        {!hideHeader && (title || onClose) && (
          <div className="fm-modal__header">
            {title && <h2 id={titleId} className="fm-modal__title">{title}</h2>}
            {onClose && (
              <button
                type="button"
                className="fm-modal__close"
                onClick={onClose}
                aria-label="Close dialog"
              >
                <XIcon size={18} />
              </button>
            )}
          </div>
        )}

        <div className={cn("fm-modal__body", bodyClassName)}>{children}</div>

        {footer && <div className="fm-modal__footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   TOAST
   
   Module-level store + viewport. No provider required. Mount the
   <ToastViewport /> once anywhere in your tree (root is fine).
   
   API:
     toast.show(message, opts)          // info-styled
     toast.success(message, opts)
     toast.warning(message, opts)
     toast.error(message, opts)
     toast.dismiss(id)
   
   opts: { title?, duration?, variant? }
   default duration: 4000ms; pass 0 / Infinity to make it sticky.
═══════════════════════════════════════════════════════════════════════ */
const toastStore = (() => {
  let items = [];
  let nextId = 1;
  const listeners = new Set();

  const emit = () => listeners.forEach((l) => l(items));

  return {
    show(opts) {
      const id = nextId++;
      const item = {
        id,
        message: opts.message,
        title: opts.title,
        variant: opts.variant || "info",
        duration: opts.duration ?? 4000,
      };
      items = [...items, item];
      emit();
      if (item.duration && Number.isFinite(item.duration)) {
        setTimeout(() => this.dismiss(id), item.duration);
      }
      return id;
    },
    dismiss(id) {
      items = items.filter((t) => t.id !== id);
      emit();
    },
    subscribe(fn) {
      listeners.add(fn);
      fn(items);
      return () => listeners.delete(fn);
    },
  };
})();

/** Public toast API — call from anywhere, even outside React. */
export const toast = {
  show: (message, opts = {}) => toastStore.show({ message, ...opts }),
  info: (message, opts = {}) => toastStore.show({ message, ...opts, variant: "info" }),
  success: (message, opts = {}) => toastStore.show({ message, ...opts, variant: "success" }),
  warning: (message, opts = {}) => toastStore.show({ message, ...opts, variant: "warning" }),
  error: (message, opts = {}) => toastStore.show({ message, ...opts, variant: "danger" }),
  dismiss: (id) => toastStore.dismiss(id),
};

const VARIANT_ICON = {
  success: CheckIcon,
  warning: AlertIcon,
  danger:  AlertIcon,
  info:    InfoIcon,
};

/**
 * ToastViewport — renders the live toast stack. Mount once.
 * Portalled so it sits above modals, panels, etc.
 */
export function ToastViewport() {
  const [items, setItems] = useState([]);

  useEffect(() => toastStore.subscribe(setItems), []);

  if (typeof document === "undefined" || items.length === 0) return null;

  return createPortal(
    <div className="fm-toast-viewport" role="region" aria-label="Notifications" aria-live="polite">
      {items.map((t) => {
        const Icon = VARIANT_ICON[t.variant] || InfoIcon;
        return (
          <div key={t.id} className={cn("fm-toast", `fm-toast--${t.variant}`)} role={t.variant === "danger" ? "alert" : "status"}>
            <span className="fm-toast__icon"><Icon size={18} /></span>
            <div className="fm-toast__body">
              {t.title && <div className="fm-toast__title">{t.title}</div>}
              <div className="fm-toast__message">{t.message}</div>
            </div>
            <button
              type="button"
              className="fm-toast__close"
              onClick={() => toastStore.dismiss(t.id)}
              aria-label="Dismiss notification"
            >
              <XIcon size={14} />
            </button>
          </div>
        );
      })}
    </div>,
    document.body,
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   DEFAULT EXPORT — convenience namespace
═══════════════════════════════════════════════════════════════════════ */
const FacemeetUI = {
  Button,
  Input,
  Modal,
  Spinner,
  ThemeToggle,
  ToastViewport,
  useTheme,
  toast,
  cn,
  icons: { SunIcon, MoonIcon, SystemIcon, XIcon, CheckIcon, AlertIcon, InfoIcon },
};

export default FacemeetUI;